import React, {
  useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Col, FormGroup, Input, Label, Row,
} from 'reactstrap';
import { CampaignContext } from '../context/CampaignWrapper';
import RequiredAsterisk from '../pages/Checkout/components/RequiredAsterisk';

export type Address = { route?: string, streetNumber?: string, postcode?: string, locality?: string };

interface IGooglePlacesComponent {
  value?: Address
  onChange: (e: { target: { name: string, value: string | Address } }) => void
}

export const formatAddress = (address: Address) => `${address?.route} ${address?.streetNumber}, \
${address?.postcode} ${address?.locality}`.replace(/\s?undefined\s?/g, '');

const GooglePlacesComponent = ({ value, onChange: _onChange }:IGooglePlacesComponent) => {
  const { campaign: { organisation: { locale }, deliveryOptions, textConfig } } = useContext(CampaignContext);
  const [showFullForm, setShowFullForm] = useState(false);
  const [fields, setFields] = useState<Address>({});
  const [translate] = useTranslation();

  const { centralAddress, radius } = deliveryOptions?.deliveryRadii?.[0] || {};
  const [lng, lat] = centralAddress?.coordinates || [];

  const address1Ref = useRef<HTMLInputElement>(null);
  const address2Ref = useRef<HTMLInputElement>(null);
  const postalRef = useRef<HTMLInputElement>(null);
  const localityRef = useRef<HTMLInputElement>(null);

  const onChange = useCallback(({
    route, streetNumber, postcode, locality,
  }: Address) => {
    const newFields = {
      route: route || fields.route,
      streetNumber: streetNumber || fields.streetNumber,
      postcode: postcode || fields.postcode,
      locality: locality || fields.locality,
    };
    _onChange({
      target: {
        name: 'address',
        value: newFields,
      },
    });
    setFields(newFields);
  }, [fields, _onChange]);

  useEffect(() => {
    if (address1Ref.current) address1Ref.current.focus();
  }, []);

  useEffect(() => {
    if (address1Ref.current) {
      const deliveryRestrictions = lng && lat && radius ? {
        location: { lng, lat },
        radius,
      } : {};

      const autocomplete = new google.maps.places.Autocomplete(address1Ref.current, {
        componentRestrictions: {
          country: deliveryOptions?.deliveryCountries?.length
            ? deliveryOptions.deliveryCountries
            : locale.slice(-2).toLowerCase(),
        },
        ...deliveryRestrictions,
        fields: ['address_components', 'geometry'],
        types: ['address'],
      });

      autocomplete.addListener('place_changed', () => {
        setShowFullForm(true);
        // Get the place details from the autocomplete object.
        const place = autocomplete.getPlace();
        let route = '';
        let streetNumber = '';
        let postcode = '';
        let locality = '';

        // Get each component of the address from the place details, and then fill-in the corresponding field on the form.
        // place.address_components are google.maps.GeocoderAddressComponent objects
        // which are documented at http://goo.gle/3l5i5Mr
        (place.address_components as google.maps.GeocoderAddressComponent[]).forEach((component) => {
          const componentType = component.types[0];

          switch (componentType) {
            case 'street_number': {
              streetNumber = component.long_name;
              break;
            }

            case 'route': {
              route += component.short_name;
              break;
            }

            case 'postal_code': {
              postcode = `${component.long_name}${postcode}`;
              break;
            }

            case 'postal_code_suffix': {
              postcode = `${postcode}-${component.long_name}`;
              break;
            }

            case 'locality':
              locality = component.long_name;
              break;

            default: break;
          }
        });

        if (address1Ref.current) address1Ref.current.value = route;
        if (address2Ref.current) address2Ref.current.value = streetNumber;
        if (postalRef.current) postalRef.current.value = postcode;
        if (localityRef.current) localityRef.current.value = locality;

        // After filling the form with address components from the Autocomplete
        // prediction, set cursor focus on the second address line to encourage
        // entry of subpremise information such as apartment, unit, or floor number.
        if (address2Ref.current) address2Ref.current.focus();

        onChange({
          route, streetNumber, postcode, locality,
        });
      });
    }
  }, [address1Ref, deliveryOptions?.deliveryCountries, lat, lng, locale, onChange, radius]);

  return (
    <div>
      <FormGroup>
        <Label htmlFor="ship-address" className="mb-0">
          {/* Avoid the word "address" in id, name, or label text to avoid browser autofill from conflicting with
          Place Autocomplete. Star or comment bug https://crbug.com/587466 to request Chromium to honor autocomplete="off"
          attribute. */}
          {textConfig.addressLabel || translate('COMPONENTS.CHECKOUT_CONTAINER.FORM.SHIPPING_ADDRESS')}
          <RequiredAsterisk />
        </Label>
        <Input
          id="ship-address"
          name="ship-address"
          required
          autoComplete="off"
          innerRef={address1Ref}
          placeholder={translate('COMPONENTS.CHECKOUT_CONTAINER.FORM.ADDRESS_PLACEHOLDER')}
          defaultValue={value ? formatAddress(value) : ''}
        />
      </FormGroup>
      {showFullForm && (
      <>
        <Row>
          <Col>
            <FormGroup>
              <Label htmlFor="address2">
                {translate('COMPONENTS.CHECKOUT_CONTAINER.FORM.STREET_NUMBER')}
                <RequiredAsterisk />
              </Label>
              <Input
                id="address2"
                name="address2"
                required
                innerRef={address2Ref}
                onChange={(e) => onChange({ streetNumber: e.target.value })}
              />
            </FormGroup>
          </Col>
          <Col>
            <FormGroup>
              <Label htmlFor="postcode">
                {translate('COMPONENTS.CHECKOUT_CONTAINER.FORM.POSTAL_CODE')}
                <RequiredAsterisk />
              </Label>
              <Input
                id="postcode"
                name="postcode"
                required
                innerRef={postalRef}
                onChange={(e) => onChange({ postcode: e.target.value })}
              />
            </FormGroup>
          </Col>
        </Row>
        <FormGroup>
          <Label htmlFor="locality">
            {translate('COMPONENTS.CHECKOUT_CONTAINER.FORM.CITY')}
            <RequiredAsterisk />
          </Label>
          <Input
            id="locality"
            name="locality"
            required
            innerRef={localityRef}
            onChange={(e) => onChange({ locality: e.target.value })}
          />
        </FormGroup>
      </>
      )}
    </div>
  );
};

export default GooglePlacesComponent;
